import React, { useRef, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Icon } from "../../components/Component";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import { formatCurrency, formatDecimal } from "../../utils/Utils";

interface WorkOrderPdfGeneratorProps {
  workOrderData: any;
  orgLogo?: string;
  orgName?: string;
  getPdfUrl?: (blob: Blob) => void;
  triggerAttribute?: string;
  onPdfGenerated?: () => void;
}

const WorkOrderPdfGenerator: React.FC<WorkOrderPdfGeneratorProps> = ({
  workOrderData,
  orgLogo,
  orgName = "Your Organization",
  getPdfUrl,
  triggerAttribute,
  onPdfGenerated,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (download: boolean = false) => {
    console.log("=== PDF Generation Started ===");
    console.log("workOrderData:", workOrderData);
    console.log("workOrderData.workOrderNumber:", workOrderData?.workOrderNumber);
    console.log("workOrderData.workOrderLines:", workOrderData?.workOrderLines?.length, "lines");
    console.log("componentRef.current:", componentRef.current);

    if (!componentRef.current) {
      console.error("componentRef.current is null!");
      return;
    }

    try {
      setIsGenerating(true);

      const element = componentRef.current;

      console.log("Element dimensions:", {
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
        innerHTML_length: element.innerHTML.length,
      });

      // Element is already positioned off-screen, just wait for any pending renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Use html2canvas to capture the component
      console.log("Starting html2canvas...");
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: true, // Enable logging for debugging
        onclone: (clonedDoc, clonedElement) => {
          console.log("onclone called, clonedElement:", {
            offsetWidth: clonedElement.offsetWidth,
            offsetHeight: clonedElement.offsetHeight,
          });

          // Ensure the cloned element is visible
          clonedElement.style.display = "block";
          clonedElement.style.visibility = "visible";
          clonedElement.style.position = "relative";
          clonedElement.style.left = "0";
          clonedElement.style.top = "0";

          const style = clonedDoc.createElement("style");
          style.innerHTML = `
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            [data-pdf-template] {
              display: block !important;
              visibility: visible !important;
            }
          `;
          clonedDoc.head.appendChild(style);
        },
      });

      console.log("html2canvas completed. Canvas dimensions:", {
        width: canvas.width,
        height: canvas.height,
      });

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate dimensions
      const marginTop = 8; // mm
      const marginBottom = 10; // mm for page numbers
      const marginSide = 5; // mm
      const contentWidth = pdfWidth - marginSide * 2;
      const contentHeight = pdfHeight - marginTop - marginBottom;

      // Convert canvas dimensions to PDF dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Calculate total pages needed
      const totalPages = Math.ceil(imgHeight / contentHeight);

      console.log("PDF dimensions:", {
        pdfWidth,
        pdfHeight,
        contentWidth,
        contentHeight,
        imgWidth,
        imgHeight,
        totalPages,
      });

      // Process each page
      for (let page = 0; page < totalPages; page++) {
        console.log(`Processing page ${page + 1} of ${totalPages}`);

        if (page > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image to show on this page
        const sourceY = (page * contentHeight * canvas.width) / contentWidth;
        const sourceHeight = Math.min(
          (contentHeight * canvas.width) / contentWidth,
          canvas.height - sourceY
        );

        console.log(`Page ${page + 1} - sourceY: ${sourceY}, sourceHeight: ${sourceHeight}`);

        if (sourceHeight > 0) {
          // Create temporary canvas for this page
          const pageCanvas = document.createElement("canvas");
          const pageCtx = pageCanvas.getContext("2d");

          if (pageCtx) {
            // Set canvas size
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;

            // Fill with white background
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

            // Draw the content for this page
            pageCtx.drawImage(
              canvas,
              0,
              sourceY, // source coordinates
              canvas.width,
              sourceHeight, // source dimensions
              0,
              0, // destination coordinates
              canvas.width,
              sourceHeight // destination dimensions
            );

            // Convert to image data
            const pageImgData = pageCanvas.toDataURL("image/png", 0.95);

            // Calculate PDF dimensions for this page segment
            const pdfContentHeight = (sourceHeight * contentWidth) / canvas.width;

            // Add image to PDF
            console.log(`Adding image to PDF - page ${page + 1}, dimensions: ${contentWidth}x${pdfContentHeight}`);
            pdf.addImage(
              pageImgData,
              "PNG",
              marginSide, // x position
              marginTop, // y position
              contentWidth, // width
              pdfContentHeight, // height
              undefined,
              "FAST"
            );
            console.log(`Image added to page ${page + 1}`);
          }
        }

        // Add page number at the bottom
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${page + 1} of ${totalPages}`,
          pdfWidth - 30,
          pdfHeight - 5 // Fixed position at bottom
        );
      }

      console.log("=== PDF Generation Completed ===");

      // Generate blob
      const generatedPdfBlob = pdf.output("blob");
      console.log("PDF Blob size:", generatedPdfBlob.size, "bytes");
      setPdfBlob(generatedPdfBlob);

      // Create URL for preview
      const url = URL.createObjectURL(generatedPdfBlob);
      setPdfUrl(url);

      // Generate blob for email attachment if callback provided
      if (getPdfUrl) {
        getPdfUrl(generatedPdfBlob);
      }

      if (download) {
        // Download the PDF
        pdf.save(
          `Work_Order_${workOrderData.workOrderNumber}_${dayjs().format(
            "DDMMYYYY"
          )}.pdf`
        );
      }

      setIsGenerating(false);
      return generatedPdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsGenerating(false);
      throw error;
    }
  };

  const handlePreview = async () => {
    try {
      await generatePDF(false);
      setShowPreview(true);
      onPdfGenerated?.();
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      onPdfGenerated?.();
    }
  };

  // Helper function to restore body scroll
  const restoreBodyScroll = () => {
    // Use setTimeout to ensure modal has fully closed
    setTimeout(() => {
      // Remove the style attribute entirely to restore to normal state
      document.body.removeAttribute("style");
      document.body.classList.remove("modal-open");
      // Remove any lingering backdrop
      const backdrops = document.querySelectorAll(".modal-backdrop");
      backdrops.forEach((backdrop) => backdrop.remove());
    }, 300);
  };

  const handleDownload = async () => {
    try {
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Work_Order_${
          workOrderData.workOrderNumber
        }_${dayjs().format("DDMMYYYY")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        await generatePDF(true);
      }
      setShowPreview(false);
      restoreBodyScroll();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    setPdfBlob(null);
    restoreBodyScroll();
  };

  const getStatusColor = (status: string) => {
    const statusColors: any = {
      Draft: "#6c757d",
      Submitted: "#0dcaf0",
      "Pending Approval": "#ffc107",
      Approved: "#198754",
      Released: "#20c997",
      "Contractor Confirmed": "#1890ff",
      "In Execution": "#17a2b8",
      "Changes Requested": "#fd7e14",
      Rejected: "#dc3545",
      Completed: "#198754",
      Closed: "#6c757d",
      Cancelled: "#6c757d",
    };
    return statusColors[status] || "#6c757d";
  };

  return (
    <>
      {/* PDF Generation Button */}
      <Button
        className="btn btn-white btn-outline-light"
        onClick={handlePreview}
        disabled={isGenerating}
        {...(triggerAttribute ? { [triggerAttribute]: "true" } : {})}
      >
        {isGenerating ? (
          <>
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="sr-only">Loading...</span>
            </div>
            Generating...
          </>
        ) : (
          <>
            <Icon name="download" />
            <span>Export PDF</span>
          </>
        )}
      </Button>

      {/* PDF Preview Modal */}
      <Modal
        isOpen={showPreview}
        toggle={closePreview}
        size="xl"
        className="modal-dialog-centered"
        backdrop="static"
        scrollable={true}
        onClosed={() => {
          // This runs after modal animation completes - add delay to ensure it's the last thing
          setTimeout(() => {
            document.body.removeAttribute("style");
          }, 100);
        }}
      >
        <ModalHeader toggle={closePreview}>
          <div className="d-flex align-items-center">
            <Icon name="file-docs" className="me-2" />
            Work Order PDF Preview - {workOrderData.workOrderNumber}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0, maxHeight: "70vh", overflow: "auto" }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted small">
              <Icon name="info" className="me-1" />
              Preview generated on {dayjs().format("DD-MMM-YYYY HH:mm")}
            </div>
            <div>
              <Button color="primary" onClick={handleDownload}>
                <Icon name="download" className="me-1" />
                Download PDF
              </Button>
              <Button color="secondary" onClick={closePreview} className="ms-2">
                Close
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Hidden PDF Template - Using position off-screen instead of display:none for proper rendering */}
      <div
        ref={componentRef}
        data-pdf-template="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "0",
          width: "210mm",
          minHeight: "297mm",
          padding: "15px",
          backgroundColor: "white",
          fontFamily: "'Arial', sans-serif",
          fontSize: "11px",
          lineHeight: "1.4",
          color: "#333",
          zIndex: -1000,
        }}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: "2px solid #1890ff",
            paddingBottom: "12px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {orgLogo && (
              <img
                src={orgLogo}
                alt={orgName}
                style={{
                  height: "40px",
                  marginRight: "12px",
                }}
              />
            )}
            <div>
              <h1
                style={{
                  color: "#333",
                  margin: "0",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {orgName || workOrderData.orgName}
              </h1>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <h2
              style={{
                color: "#1890ff",
                margin: "0 0 4px 0",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              WORK ORDER
            </h2>
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              {workOrderData.workOrderNumber}
            </div>
            <div style={{ fontSize: "10px", color: "#666" }}>
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: getStatusColor(workOrderData.status),
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "3px",
                  fontSize: "9px",
                }}
              >
                {workOrderData.status || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Section 1: General Information - Matching View Screen Descriptions style */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            📋 General Information
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px",
              border: "1px solid #f0f0f0",
            }}
          >
            <tbody>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                  Work Order Number
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {workOrderData.workOrderNumber || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold", width: "20%" }}>
                  Contract Date
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {workOrderData.contractDate ? dayjs(workOrderData.contractDate).format("DD-MMM-YYYY") : "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Work Order Title
                </td>
                <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.workOrderTitle || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Site
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.siteName || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Project
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.projectName || "Not selected"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Contract Type
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.contractType || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Contract With
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.contractWith || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Contractor
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.contractorName || "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Work Order Owner
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.workOrderOwnerName || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Planned Start
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.plannedStartDate ? dayjs(workOrderData.plannedStartDate).format("DD-MMM-YYYY") : "-"}
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Planned End
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {workOrderData.plannedEndDate ? dayjs(workOrderData.plannedEndDate).format("DD-MMM-YYYY") : "-"}
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Approval Required
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: workOrderData.approvalRequired ? "#fa8c16" : "#52c41a",
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {workOrderData.approvalRequired ? "Yes" : "No"}
                  </span>
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                  Tax Enabled
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: workOrderData.enableTax ? "#1890ff" : "#d9d9d9",
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {workOrderData.enableTax ? `Yes (${workOrderData.taxApplicationLevel || "Line"})` : "No"}
                  </span>
                </td>
              </tr>
              {workOrderData.enableTax && (workOrderData.headerTaxName || workOrderData.tdsTaxName || workOrderData.tcsTaxName) && (
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Tax Details
                  </td>
                  <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "6px 10px", fontSize: "9px" }}>
                    {workOrderData.headerTaxName && (
                      <span style={{ marginRight: "12px" }}>
                        <span style={{ backgroundColor: "#13c2c2", color: "white", padding: "1px 4px", borderRadius: "2px", marginRight: "4px" }}>Header Tax</span>
                        {workOrderData.headerTaxName}
                      </span>
                    )}
                    {workOrderData.tdsTaxName && (
                      <span style={{ marginRight: "12px" }}>
                        <span style={{ backgroundColor: "#fa8c16", color: "white", padding: "1px 4px", borderRadius: "2px", marginRight: "4px" }}>TDS</span>
                        {workOrderData.tdsTaxName} (Deducted)
                      </span>
                    )}
                    {workOrderData.tcsTaxName && (
                      <span>
                        <span style={{ backgroundColor: "#722ed1", color: "white", padding: "1px 4px", borderRadius: "2px", marginRight: "4px" }}>TCS</span>
                        {workOrderData.tcsTaxName} (Added)
                      </span>
                    )}
                  </td>
                </tr>
              )}
              {workOrderData.budgetName && (
                <tr>
                  <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", backgroundColor: "#fafafa", fontWeight: "bold" }}>
                    Budget
                  </td>
                  <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                    <span style={{ backgroundColor: "#722ed1", color: "white", padding: "1px 6px", borderRadius: "2px", fontSize: "9px" }}>
                      {workOrderData.budgetName}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Description */}
        {workOrderData.description && (
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontWeight: "bold", fontSize: "10px", marginBottom: "4px" }}>Description:</div>
            <div
              style={{
                backgroundColor: "#f9f9f9",
                border: "1px solid #f0f0f0",
                padding: "8px",
                fontSize: "10px",
                borderRadius: "4px",
              }}
            >
              {workOrderData.description}
            </div>
          </div>
        )}

        {/* Section 2: Work Order Lines */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>📋 Work Order Lines ({workOrderData.workOrderLines?.length || 0})</span>
          </div>

          {/* Tags row like view screen */}
          <div style={{ marginBottom: "8px", fontSize: "9px" }}>
            <span style={{ backgroundColor: "#1890ff", color: "white", padding: "2px 8px", borderRadius: "3px", marginRight: "6px" }}>
              {workOrderData.workOrderLines?.length || 0} line(s)
            </span>
            {workOrderData.budgetName && (
              <span style={{ backgroundColor: "#722ed1", color: "white", padding: "2px 8px", borderRadius: "3px", marginRight: "6px" }}>
                Budget: {workOrderData.budgetName}
              </span>
            )}
            {workOrderData.segmentLevel && (
              <span style={{ backgroundColor: "#fa8c16", color: "white", padding: "2px 8px", borderRadius: "3px", marginRight: "6px" }}>
                Work Level: {workOrderData.segmentLevel}
              </span>
            )}
            {workOrderData.workType && (
              <span style={{ backgroundColor: "#eb2f96", color: "white", padding: "2px 8px", borderRadius: "3px" }}>
                Work Type: {workOrderData.workType}
              </span>
            )}
          </div>

          {!workOrderData.workOrderLines || workOrderData.workOrderLines.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                backgroundColor: "#fafafa",
                border: "1px dashed #d9d9d9",
                color: "#999",
                fontSize: "11px",
              }}
            >
              No work order lines found
            </div>
          ) : (
            <>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "8px",
                  pageBreakInside: "auto",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#fafafa" }}>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "center", fontWeight: "bold", width: "4%" }}>#</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "20%" }}>Task Title</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "14%" }}>Category</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Quantity</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Rate</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "12%" }}>Tax</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "right", fontWeight: "bold", width: "10%" }}>Total Amount</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "center", fontWeight: "bold", width: "10%" }}>Source</th>
                    <th style={{ border: "1px solid #f0f0f0", padding: "6px 4px", textAlign: "left", fontWeight: "bold", width: "10%" }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrderData.workOrderLines.map((line: any, index: number) => {
                    const taxDetails = [];
                    if (line.sgstRate > 0 && line.cgstRate > 0) {
                      taxDetails.push(`SGST: ${line.sgstRate}%`);
                      taxDetails.push(`CGST: ${line.cgstRate}%`);
                    } else if (line.igstRate > 0) {
                      taxDetails.push(`IGST: ${line.igstRate}%`);
                    }

                    // Build location string
                    const locationParts = [];
                    if (line.towerVilla) locationParts.push(line.towerVilla);
                    if (line.floorName) locationParts.push(line.floorName);
                    if (line.flatName) locationParts.push(line.flatName);
                    const locationStr = locationParts.join(" / ") || "-";

                    return (
                      <tr key={index} style={{ pageBreakInside: "avoid" }}>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "center" }}>{index + 1}</td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>
                          <div style={{ fontWeight: "bold" }}>{line.taskTitle || "-"}</div>
                          {line.description && <div style={{ fontSize: "7px", color: "#666", marginTop: "2px" }}>{line.description}</div>}
                        </td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>{line.workCategoryName || "-"}</td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                          {formatDecimal(line.quantity)} {line.uomName}
                        </td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>₹{line.rate?.toLocaleString()}</td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", fontSize: "7px" }}>
                          {line.taxTypeName && <div>{line.taxTypeName}</div>}
                          {taxDetails.length > 0 && <div style={{ color: "#666" }}>{taxDetails.join(" + ")}</div>}
                          <div style={{ fontWeight: "bold" }}>₹{(line.taxAmount || 0).toLocaleString()}</div>
                        </td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right", fontWeight: "bold" }}>
                          ₹{line.lineValue?.toLocaleString()}
                        </td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "center" }}>
                          <span style={{
                            backgroundColor: line.source === "New Work Line" ? "#52c41a" : line.source === "Project Tasks" ? "#1890ff" : "#fa8c16",
                            color: "white",
                            padding: "1px 4px",
                            borderRadius: "2px",
                            fontSize: "7px",
                          }}>
                            {line.source || "-"}
                          </span>
                        </td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", fontSize: "7px" }}>{locationStr}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Summary Section - Matching view screen style */}
              <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px", fontSize: "10px" }}>
                  <span style={{ marginRight: "20px" }}>Subtotal:</span>
                  <strong>₹{(workOrderData.totalValue || 0).toLocaleString()}</strong>
                </div>
                {workOrderData.enableTax && workOrderData.taxAmount > 0 && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px", fontSize: "10px" }}>
                    <span style={{ marginRight: "20px" }}>Total Tax:</span>
                    <strong>₹{(workOrderData.taxAmount || 0).toLocaleString()}</strong>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", fontSize: "11px", borderTop: "1px solid #d9d9d9", paddingTop: "6px", marginTop: "6px" }}>
                  <span style={{ marginRight: "20px", fontWeight: "bold" }}>Gross Total:</span>
                  <strong style={{ color: "#198754", fontSize: "12px" }}>₹{(workOrderData.grossValue || 0).toLocaleString()}</strong>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Section 3: General Conditions */}
        {workOrderData.generalConditions && workOrderData.generalConditions.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              📋 General Conditions ({workOrderData.generalConditions.length})
            </div>

            <div style={{ marginBottom: "6px", fontSize: "9px" }}>
              <span style={{ backgroundColor: "#1890ff", color: "white", padding: "2px 8px", borderRadius: "3px" }}>
                {workOrderData.generalConditions.length} condition(s) selected
              </span>
            </div>

            {workOrderData.generalConditions.map((condition: any, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: "10px",
                  padding: "10px",
                  border: "1px solid #f0f0f0",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <div style={{ fontWeight: "bold", fontSize: "10px" }}>
                    📄 {index + 1}. {condition.conditionCode || `Condition ${index + 1}`}
                  </div>
                  {condition.content && (
                    <span style={{ backgroundColor: "#52c41a", color: "white", padding: "1px 6px", borderRadius: "2px", fontSize: "8px" }}>
                      Text
                    </span>
                  )}
                </div>
                {condition.content && (
                  <div style={{ backgroundColor: "#f5f5f5", padding: "8px", borderRadius: "4px", fontSize: "9px" }}>
                    <div style={{ color: "#1890ff", fontWeight: "500", marginBottom: "4px" }}>Content:</div>
                    <div style={{ whiteSpace: "pre-wrap", color: "#333" }}>{condition.content}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Section 4: Payment Conditions */}
        {workOrderData.paymentConditions && workOrderData.paymentConditions.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              📋 Payment Conditions
            </div>

            <div style={{ marginBottom: "10px", fontSize: "9px" }}>
              <span style={{ backgroundColor: "#52c41a", color: "white", padding: "2px 8px", borderRadius: "3px" }}>
                💰 Contract Value: ₹{(workOrderData.grossValue || 0).toLocaleString()}
              </span>
            </div>

            {workOrderData.paymentConditions.map((condition: any, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: "10px",
                  padding: "10px",
                  border: "1px solid #f0f0f0",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <div style={{ fontWeight: "bold", fontSize: "11px", color: "#1890ff" }}>
                    💵 {condition.conditionType || "Payment Condition"}
                  </div>
                  <div>
                    <span style={{
                      backgroundColor: condition.isPercent ? "#1890ff" : "#52c41a",
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "2px",
                      fontSize: "8px",
                      marginRight: "4px",
                    }}>
                      {condition.isPercent ? "Percentage" : "Fixed Amount"}
                    </span>
                    {condition.interestCharged && (
                      <span style={{ backgroundColor: "#fa8c16", color: "white", padding: "1px 6px", borderRadius: "2px", fontSize: "8px" }}>
                        Interest Charged
                      </span>
                    )}
                  </div>
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px" }}>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold", width: "25%" }}>Amount</td>
                      <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                        {condition.isPercent ? (
                          <>
                            <strong>{condition.percentValue}%</strong> of RABill
                            <br />
                            <span style={{ color: "#52c41a", fontSize: "8px" }}>
                              Calculated: ₹{(condition.calculatedAmount || 0).toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <>
                            <strong>₹{(condition.amountValue || 0).toLocaleString()}</strong>
                            <br />
                            <span style={{ color: "#52c41a", fontSize: "8px" }}>
                              Fixed Amount: ₹{(condition.calculatedAmount || condition.amountValue || 0).toLocaleString()}
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                    {condition.interestCharged && condition.interestRate > 0 && (
                      <tr>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold" }}>Interest Rate</td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                          <span style={{ backgroundColor: "#fa8c16", color: "white", padding: "1px 4px", borderRadius: "2px" }}>
                            {condition.interestRate}% per annum
                          </span>
                        </td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold" }}>Recovery Type</td>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                          <span style={{ backgroundColor: "#722ed1", color: "white", padding: "1px 4px", borderRadius: "2px" }}>
                            {condition.recoveryType || "-"}
                          </span>
                        </td>
                      </tr>
                    )}
                    {!condition.interestCharged && condition.recoveryType && (
                      <tr>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold" }}>Recovery Type</td>
                        <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                          <span style={{ backgroundColor: "#722ed1", color: "white", padding: "1px 4px", borderRadius: "2px" }}>
                            {condition.recoveryType}
                          </span>
                        </td>
                      </tr>
                    )}
                    {condition.conditionType === "Mobilization" && condition.recoveryPercentRate > 0 && (
                      <tr>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold" }}>Recovery %</td>
                        <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                          <span style={{ backgroundColor: "#1890ff", color: "white", padding: "1px 4px", borderRadius: "2px" }}>
                            {condition.recoveryPercentRate}% per RA Bill
                          </span>
                        </td>
                      </tr>
                    )}
                    {condition.releaseDate && (
                      <tr>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold" }}>Release Date</td>
                        <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                          <strong>{dayjs(condition.releaseDate).format("DD MMM YYYY")}</strong>
                        </td>
                      </tr>
                    )}
                    {condition.notes && (
                      <tr>
                        <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", backgroundColor: "#fafafa", fontWeight: "bold" }}>Notes</td>
                        <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                          {condition.notes}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* Section 5: Approval Hierarchy - Show only when approvalRequired is Yes */}
        {workOrderData.approvalRequired && workOrderData.approvals && workOrderData.approvals.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              📋 Approval Hierarchy
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "9px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "center", fontWeight: "bold", width: "10%" }}>Level</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "20%" }}>Role</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "left", fontWeight: "bold", width: "25%" }}>Approver Name</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "center", fontWeight: "bold", width: "20%" }}>Approval Stage</th>
                  <th style={{ border: "1px solid #f0f0f0", padding: "6px 8px", textAlign: "center", fontWeight: "bold", width: "25%" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {workOrderData.approvals.map((approval: any, index: number) => (
                  <tr key={index}>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: "#1890ff",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "10px",
                          fontSize: "8px",
                          fontWeight: "bold",
                        }}
                      >
                        {approval.level || `L${index + 1}`}
                      </span>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", fontWeight: "bold" }}>
                      {approval.approverRole || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      {approval.approverName || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: approval.approvalStage === "Approver" ? "#52c41a" : approval.approvalStage === "Checker" ? "#fa8c16" : "#1890ff",
                          color: "white",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {approval.approvalStage || "-"}
                      </span>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: approval.status === "Approved" ? "#52c41a" : approval.status === "Rejected" ? "#dc3545" : approval.status === "Pending" ? "#fa8c16" : "#6c757d",
                          color: "white",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {approval.status || "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: "10px",
            textAlign: "center",
            fontSize: "9px",
            color: "#999",
            marginTop: "20px",
          }}
        >
          Generated on {dayjs().format("DD-MMM-YYYY HH:mm")} | © {dayjs().format("YYYY")} {orgName || workOrderData.orgName}
        </div>
      </div>
    </>
  );
};

export default WorkOrderPdfGenerator;
